import { Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  ActionButton,
  Buttons,
  CancelButton,
  Field,
  Form,
  FormMessage,
  ModalCard,
  Button,
  Icon,
  Tooltip,
} from '~/components';
import { useConfirmation, useFeedback, useToast } from '~/contexts';
import { useForm } from '~/hooks';
import { useDropzone } from 'react-dropzone';
import { colors, weights } from '~/styles';
import { getCachedPreviewUrl, getFileDataUrl, getIconFromMIME } from '~/utils';
import { imagePreviewFileTypes } from '~/utils/fileTypes';
import _ from 'lodash';

const P = styled.p`
  margin-bottom: 1rem;
`;

const MAX_FILE_SIZE = 20971520; // 20mb in bytes

const ErrorMessage = styled.div`
  margin-bottom: 1.625rem;
  padding: 1rem 1.5rem;
  color: ${colors.white};
  background-color: ${colors.error};
  border-radius: 0.25rem;
`;

const DropzoneContainer = styled.div`
  display: flex;
  flex: 1;
  flex-shrink: 0;
  overflow: auto;
  min-height: 6rem;
  max-height: 12rem;
  padding-bottom: 1.625rem;
`;

const DropzoneInterior = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  border: dashed 0.125rem ${colors.grey25};
  background-color: ${colors.grey5};
  border-radius: 0.8125rem;
  font-size: 1.5rem;
  font-weight: ${weights.light};
  color: ${colors.grey40};
  cursor: pointer;
`;

const Previews = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -0.5rem;
  padding-bottom: 1.625rem;
`;

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  display: none;
  position: absolute;
  border-radius: 0.3125rem;
  justify-content: center;
  text-align: center;
  align-items: center;
`;

const Container = styled(Tooltip)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 10.5rem;
  height: 10.5rem;
  margin: 0.5rem;
  border: 1px solid ${colors.grey10};
  border-radius: 0.3125rem;
  overflow: hidden;

  &:hover ${Overlay} {
    display: flex;
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${colors.black};
  border-radius: 0.3125rem;
  opacity: 0.5;
`;

const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const PreviewIcon = styled(Icon)`
  margin-bottom: 0.5rem;
  color: ${colors.grey55};
  font-size: 3rem;
`;

const FileNameInfo = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0 1rem;
  color: ${colors.grey55};
  font-size: 0.75rem;
`;

const FileName = styled.div`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  overflow-wrap: break-word;
`;

const FileExt = styled.div`
  flex-shrink: 0;
`;

const FileButtons = styled.div`
  display: inline;
  flex: 1;
  z-index: 2;
  font-size: 0.825rem;

  & > .button {
    background-color: ${colors.white};
    z-index: 2;
    height: 2rem;
    width: 2rem;
    padding: 0;
    margin-right: 0.5rem;
  }

  & > .button:last-child {
    margin-right: 0;
  }

  & > .button:hover {
    background-color: ${colors.white};
  }
`;

const DeleteButton = styled(Button)`
  color: ${colors.danger50};
  &:hover {
    color: ${colors.danger100};
  }
`;

const supportedFiles = {
  'application/vnd.ms-excel': ['.xls'],
  'application/msword': ['.doc'],
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/bmp': ['.bmp'],
  'image/gif': ['.gif'],
  'image/tiff': ['.tif', '.tiff'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
};

function FeedbackModal({ onClose }) {
  const [{ status, message, isSubmitting }, form] = useForm();
  const { submitFeedback } = useFeedback();
  const confirmation = useConfirmation();
  const [files, setFiles] = useState([]);

  const toast = useToast();

  const initialValues = {
    subject: '',
    content: '',
  };

  async function handleSubmit(values) {
    form.submit();
    const { success, message } = await submitFeedback(values, files);

    if (success) {
      form.done();
      onClose();

      await confirmation.prompt((resolve) => (
        <ModalCard title="Feedback submitted" onClose={() => resolve(false)}>
          <ModalCard.Body>Your feedback has been submitted. Thanks for helping to make Ruddr better!</ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={() => resolve(false)}>Close</CancelButton>
            </Buttons>
          </ModalCard.Footer>
        </ModalCard>
      ));
    } else {
      form.error({ message });
    }
  }

  const addFiles = async (files) => {
    // Generate placeholder files, run in parallel
    const tempFiles = await Promise.all(
      files.map(async (fileData) => ({
        id: _.uniqueId('temp_file_'),
        name: fileData.name,
        type: fileData.type || 'application/octet-stream',
        dataUrl: await getFileDataUrl(fileData),
      })),
    );

    // Set the files all at once to prevent missing files
    setFiles((files) => [...tempFiles, ...files]);
  };

  const removeFile = (file) => {
    setFiles((files) => _.reject(files, { id: file.id }));
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    for (const file of rejectedFiles) {
      if (_.find(file.errors, { code: 'file-too-large' })) {
        toast.error(`The file ${file.file.path} is too large to upload. The file size limit is 20 MB.`);
      }
      if (_.find(file.errors, { code: 'file-invalid-type' })) {
        toast.error(
          `The file "${file.file.name}" is not supported. Allowed file types are: ${Object.values(supportedFiles)
            .flat()
            .map((ext) => ext)
            .join(', ')}.`,
        );
      }
    }
    if (acceptedFiles.length > 0) {
      addFiles(acceptedFiles);
    }
  };

  return (
    <ModalCard title="How are we doing?" onClose={onClose}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          subject: Yup.string().label('Subject').max(255).required(),
          content: Yup.string().label('Feedback').max(5000).required(),
        })}>
        <Form>
          <ModalCard.Body>
            <P>Please provide your feedback:</P>

            <Form.Control>
              <Field.Text name="subject" placeholder="Subject" maxLength={255} />
            </Form.Control>

            <Form.Control>
              <Field.TextArea name="content" placeholder="Feedback" maxLength={5000} rows={4} />
            </Form.Control>

            <FeedbackFiles key="files" files={files} onDrop={onDrop} removeFile={removeFile} />

            {status && <FormMessage.Error>{message || 'An error has occurred.'}</FormMessage.Error>}
          </ModalCard.Body>
          <ModalCard.Footer>
            <Buttons align="right">
              <CancelButton onClick={onClose}>Close</CancelButton>
              <ActionButton type="submit" isLoading={isSubmitting}>
                Submit
              </ActionButton>
            </Buttons>
          </ModalCard.Footer>
        </Form>
      </Formik>
    </ModalCard>
  );
}

function FeedbackFiles({ onDrop, files, error, removeFile }) {
  const { getRootProps, getInputProps } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    onDrop,
    accept: supportedFiles,
  });

  return (
    <>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <DropzoneContainer>
        <DropzoneInterior {...getRootProps()}>
          <input {...getInputProps()} />
          <p>Drop files here or click to select files</p>
        </DropzoneInterior>
      </DropzoneContainer>
      <Previews>
        {files.map((file) => (
          <FilePreview key={file.id} file={file} onRemove={removeFile} />
        ))}
      </Previews>
    </>
  );
}

function FilePreview({ file, onRemove }) {
  const [preview, setPreview] = useState(null);
  const [icon, setIcon] = useState(null);

  const fileNameInfo = useMemo(() => {
    const nameArray = file.name.split('.');
    if (nameArray.length < 2) {
      return {
        name: file.name,
        ext: '',
      };
    }
    const ext = '.' + nameArray.pop();
    const name = nameArray.join('.');
    return { name, ext };
  }, [file]);

  const fetchPreview = useCallback(async (id, url) => {
    const cacheKey = `feedback_item_file_preview__${id}`;
    const previewUrl = await getCachedPreviewUrl(cacheKey, url);
    setPreview(previewUrl);
  }, []);

  useEffect(() => {
    // Set the icon
    setIcon(getIconFromMIME(file.type));
    // Set or download the preview image, if available
    if (imagePreviewFileTypes.includes(file.type)) {
      if (file.dataUrl) {
        setPreview(file.dataUrl);
      } else if (file.url) {
        fetchPreview(file.id, file.url);
      }
    }
    return fetchPreview.cancel;
  }, [file, fetchPreview]);

  return (
    <Container message={preview ? file.name : undefined}>
      {preview ? (
        <PreviewImage src={preview} />
      ) : (
        <PreviewIconContainer>
          {icon && <PreviewIcon icon={icon}></PreviewIcon>}
          <FileNameInfo>
            <FileName>{fileNameInfo.name}</FileName>
            <FileExt>{fileNameInfo.ext}</FileExt>
          </FileNameInfo>
        </PreviewIconContainer>
      )}
      <Overlay>
        <FileButtons>
          <DeleteButton isAnchor onClick={() => onRemove(file)}>
            <Icon icon="trash" />
          </DeleteButton>
        </FileButtons>
        <Background />
      </Overlay>
    </Container>
  );
}

export default FeedbackModal;
